/**
 * Het reizende power-icoon volgt het pad van de grafische lijnen door de pagina.
 *
 * De lijnen zijn CSS-randen op losse elementen, verspreid over meerdere blokken.
 * Dit script leest hun werkelijke geometrie uit (rect plus randbreedtes plus
 * hoekradius), zet elk element om in een reeks punten in documentcoördinaten,
 * en rijgt die aan elkaar tot één pad.
 *
 * Elk blok met een lijn brengt zijn eigen icoon mee (`_components/line-icon`).
 * Het icoon zoekt zijn lijnstukken binnen zijn eigen blok en loopt het pad af
 * terwijl dat blok door beeld schuift: onderaan in beeld is 0%, bovenaan eruit
 * is 100%. Daarbuiten is het icoon er niet.
 *
 * Een element doet mee zodra het `data-line` heeft. De vorm wordt afgeleid uit
 * de randen die getekend zijn:
 *   één rand            een recht stuk
 *   twee aangrenzende   een L met een bocht op de gedeelde hoek
 *   geen randen         een dun blok, dus een recht stuk langs de lange as
 * Voor een lijn die geen rand is (de golvende SVG bij de stappen) staat er een
 * onzichtbaar meetelement met een transparante rand: dat levert alleen de
 * geometrie, geen tweede lijn.
 */

const KIES_ICOON = "[data-line-travel]";
const KIES_SEGMENT = "[data-line]";
const TOLERANTIE = 14; // px waarbinnen twee uiteinden als verbonden gelden
const BOOGSTAPPEN = 10;
/** Radius van de bocht in een verbindingsstuk, gelijk aan die van de sierlijnen. */
const VERBIND_RADIUS = 32;
/** Zo ver buiten beeld mag het icoon beginnen en eindigen; net meer dan zijn halve maat. */
const OVERSCHOT = 24;

const getal = (v) => parseFloat(v) || 0;
const afstand = (a, b) => Math.hypot(b[0] - a[0], b[1] - a[1]);

function documentRect(el) {
    const r = el.getBoundingClientRect();
    return {
        left: r.left + window.scrollX,
        top: r.top + window.scrollY,
        right: r.right + window.scrollX,
        bottom: r.bottom + window.scrollY,
        width: r.width,
        height: r.height,
    };
}

function boog(cx, cy, r, van, naar) {
    const punten = [];
    for (let i = 1; i <= BOOGSTAPPEN; i++) {
        const a = van + (naar - van) * (i / BOOGSTAPPEN);
        punten.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r]);
    }
    return punten;
}

/** Zet één lijnelement om in een reeks punten in documentcoördinaten. */
function segmentPunten(el) {
    const cs = getComputedStyle(el);
    if (cs.display === "none" || cs.visibility === "hidden") return null;

    const bt = getal(cs.borderTopWidth);
    const br = getal(cs.borderRightWidth);
    const bb = getal(cs.borderBottomWidth);
    const bl = getal(cs.borderLeftWidth);
    const rect = documentRect(el);
    if (rect.width < 1 && rect.height < 1) return null;

    const L = rect.left, T = rect.top, R = rect.right, B = rect.bottom;
    // hartlijnen: het midden van de rand, niet de buitenkant van de doos
    const xl = L + bl / 2, xr = R - br / 2, yt = T + bt / 2, yb = B - bb / 2;
    // Bewust booleans en geen samengestelde string: die string bouwde ik op als
    // T-R-B-L, dus rechts plus onder werd "RB" terwijl ik op "BR" vergeleek. Die
    // tak matchte daardoor nooit en de onderbocht van het CTA-blok viel terug op
    // een rechte lijn.
    const heeftT = bt > 0, heeftR = br > 0, heeftB = bb > 0, heeftL = bl > 0;
    const aantal = (heeftT ? 1 : 0) + (heeftR ? 1 : 0) + (heeftB ? 1 : 0) + (heeftL ? 1 : 0);

    // Geen randen: een dun blok. De lange as is de lijn.
    if (aantal === 0) {
        if (rect.height <= rect.width) {
            const y = T + rect.height / 2;
            return [[L, y], [R, y]];
        }
        const x = L + rect.width / 2;
        return [[x, T], [x, B]];
    }

    if (aantal === 1) {
        if (heeftL) return [[xl, T], [xl, B]];
        if (heeftR) return [[xr, T], [xr, B]];
        if (heeftT) return [[L, yt], [R, yt]];
        return [[L, yb], [R, yb]];
    }

    // Een bocht heeft twee radii: de buitenradius uit de style, en de radius van
    // de hartlijn van de rand. Het middelpunt van de bocht volgt uit de
    // BUITENradius (op `radius` afstand van beide randen van de doos), en de
    // hartlijn draait daar met `buiten - randdikte / 2` omheen. Die twee door
    // elkaar halen was mijn fout: dan ligt het middelpunt verkeerd en loopt het
    // rechte stuk ervoor de andere kant op, waardoor het icoon de bocht mist.
    const hoekRadius = (hoek, d1, d2) => {
        const buiten = getal(cs[`border${hoek}Radius`]);
        return { buiten, hart: Math.max(buiten - (d1 + d2) / 4, 0) };
    };

    // Twee aangrenzende randen. De volgorde loopt telkens naar beneden of naar
    // buiten; het script draait een segment later om als dat beter op zijn
    // voorganger aansluit.
    if (heeftT && heeftL) {
        const { buiten, hart } = hoekRadius("TopLeft", bt, bl);
        const cx = L + buiten, cy = T + buiten;
        return [[R, yt], [cx, yt], ...boog(cx, cy, hart, -Math.PI / 2, -Math.PI), [xl, B]];
    }
    if (heeftT && heeftR) {
        const { buiten, hart } = hoekRadius("TopRight", bt, br);
        const cx = R - buiten, cy = T + buiten;
        return [[L, yt], [cx, yt], ...boog(cx, cy, hart, -Math.PI / 2, 0), [xr, B]];
    }
    if (heeftB && heeftL) {
        const { buiten, hart } = hoekRadius("BottomLeft", bb, bl);
        const cx = L + buiten, cy = B - buiten;
        return [[xl, T], [xl, cy], ...boog(cx, cy, hart, Math.PI, Math.PI / 2), [R, yb]];
    }
    if (heeftB && heeftR) {
        const { buiten, hart } = hoekRadius("BottomRight", bb, br);
        const cx = R - buiten, cy = B - buiten;
        return [[xr, T], [xr, cy], ...boog(cx, cy, hart, 0, Math.PI / 2), [L, yb]];
    }

    // Drie of vier randen komen niet voor in dit design; val terug op de omtrek
    // van boven naar onder langs de linkerkant.
    return [[xl, T], [xl, B]];
}

/**
 * Ligt `punt` op een van de segmenten van `punten`? Zo ja, op welke plek. Wordt
 * gebruikt om een stuk af te knippen dat over zijn voorganger heen valt.
 */
function raakpunt(punten, punt, marge = 8) {
    for (let i = 1; i < punten.length; i++) {
        const a = punten[i - 1], b = punten[i];
        const dx = b[0] - a[0], dy = b[1] - a[1];
        const lengte2 = dx * dx + dy * dy;
        if (lengte2 === 0) continue;
        let t = ((punt[0] - a[0]) * dx + (punt[1] - a[1]) * dy) / lengte2;
        if (t <= 0 || t >= 1) continue;
        const afw = Math.hypot(punt[0] - (a[0] + t * dx), punt[1] - (a[1] + t * dy));
        if (afw <= marge) return { index: i };
    }
    return null;
}

/** Rekent een reeks punten om naar een stuk met zijn deellengtes. */
function maakStuk(punten, verbinding) {
    const lengtes = [];
    let lengte = 0;
    for (let i = 1; i < punten.length; i++) {
        const d = afstand(punten[i - 1], punten[i]);
        lengtes.push(d);
        lengte += d;
    }
    return { punten, lengtes, lengte, verbinding };
}

/**
 * Het verbindingsstuk tussen het einde van de ene lijn en het begin van de
 * volgende. Orthogonaal met één bocht, zodat het als een verlenging van de lijn
 * leest en niet als een schuine sprong: eerst verticaal vanaf de uitgang naar de
 * hoogte van de ingang, dan horizontaal ernaartoe. Valt een van beide weg omdat
 * de punten al op één lijn liggen, dan blijft er een recht stuk over.
 */
function verbindingsPunten(uit, in_) {
    const dx = in_[0] - uit[0];
    const dy = in_[1] - uit[1];
    if (Math.abs(dx) < 2) return [uit, in_];
    if (Math.abs(dy) < 2) return [uit, in_];

    const r = Math.min(VERBIND_RADIUS, Math.abs(dx), Math.abs(dy));
    const hoek = [uit[0], in_[1]];
    // middelpunt van de bocht: r terug langs de verticaal, r vooruit langs de horizontaal
    const cx = uit[0] + Math.sign(dx) * r;
    const cy = in_[1] - Math.sign(dy) * r;
    const vanHoek = Math.sign(dy) > 0 ? Math.PI : 0;              // punt recht boven of onder het middelpunt
    const naarHoek = Math.sign(dx) > 0
        ? (Math.sign(dy) > 0 ? Math.PI / 2 : -Math.PI / 2)
        : (Math.sign(dy) > 0 ? Math.PI / 2 : -Math.PI / 2);
    const startBoog = [uit[0], cy];
    const eindBoog = [cx, in_[1]];
    const boogPunten = [];
    for (let i = 1; i < BOOGSTAPPEN; i++) {
        const f = i / BOOGSTAPPEN;
        // kwartcirkel van startBoog naar eindBoog om (cx, cy)
        const a0 = Math.atan2(startBoog[1] - cy, startBoog[0] - cx);
        let a1 = Math.atan2(eindBoog[1] - cy, eindBoog[0] - cx);
        // kortste weg
        let d = a1 - a0;
        while (d > Math.PI) d -= 2 * Math.PI;
        while (d < -Math.PI) d += 2 * Math.PI;
        const a = a0 + d * f;
        boogPunten.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r]);
    }
    return [uit, startBoog, ...boogPunten, eindBoog, in_];
}

/**
 * Bouwt uit alle lijnelementen één lijst van stukken. Elk stuk is
 * `{ punten, lengtes, lengte, gat }`, en tussen twee stukken die niet op elkaar
 * aansluiten zit een gat met zijn eigen lengte.
 *
 * Twee dingen zijn hier belangrijk:
 *
 * 1. De lijnen bleeden met `-50vw` tot buiten het scherm. Dat onzichtbare deel
 *    telt niet mee: we klemmen elke x binnen de vensterbreedte. Zonder dat
 *    besteedt het icoon het grootste deel van de scroll aan stukken die niemand
 *    ziet, ver naast de pagina.
 * 2. Het pad moet van boven naar onder lopen, want de voortgang hangt aan de
 *    scrollpositie. We sorteren de stukken dus op hun hoogste punt en lopen elk
 *    stuk in de richting van toenemende y. Een horizontaal stuk heeft geen
 *    richting in y; dat oriënteren we op het uiteinde dat het dichtst bij de
 *    uitgang van het vorige stuk ligt.
 */
function bouwPad(wortels) {
    const breedte = document.documentElement.clientWidth;
    // Een halve lijndikte naar binnen klemmen. Klem je op 0 en op de volle
    // breedte, dan valt de helft van een verbinding die langs de rand loopt buiten
    // beeld en lijkt die 3px dun in plaats van 6px.
    const rand = 3;

    // Eerst verzamelen, dan pas klemmen: alleen het eerste en het laatste stuk
    // mogen voorbij de schermrand doorlopen, zodat het icoon net buiten beeld
    // begint en eindigt in plaats van op de rand te verschijnen.
    const verzameld = [];
    const gevonden = [];
    for (const w of [].concat(wortels)) gevonden.push(...w.querySelectorAll(KIES_SEGMENT));
    for (const el of gevonden) {
        const punten = segmentPunten(el);
        if (!punten || punten.length < 2) continue;
        verzameld.push({ el, punten });
    }

    const rauw = [];
    verzameld.forEach((v, i) => {
        const buiten = i === 0 || i === verzameld.length - 1 ? OVERSCHOT : 0;
        const geklemd = v.punten.map(([x, y]) => [
            Math.min(Math.max(x, rand - buiten), breedte - rand + buiten),
            y,
        ]);
        // volledig buiten beeld of tot niets samengevallen
        let lengte = 0;
        for (let j = 1; j < geklemd.length; j++) lengte += afstand(geklemd[j - 1], geklemd[j]);
        if (lengte < 2) return;
        rauw.push({ el: v.el, punten: geklemd });
    });

    // De DOM-volgorde is de bedoelde volgorde: de content-builder rendert de
    // blokken op paginavolgorde, en binnen een blok staan de lijnstukken in de
    // volgorde waarin ze gevolgd moeten worden. Sorteren op het hoogste punt gaat
    // juist fout zodra twee stukken dezelfde bovenkant hebben, zoals de twee helften
    // van een Z: dan wisselt hun onderlinge orde en loopt het pad terug omhoog.
    const horizontaal = (p) => Math.abs(p[p.length - 1][1] - p[0][1]) < 2;
    // Richting: elk niet-horizontaal stuk loopt naar beneden. Dat gaat vóór
    // nabijheid. Kies je per stuk het dichtstbijzijnde uiteinde bij de vorige
    // uitgang, dan wordt het paneel op de home omgekeerd (zijn onderkant ligt
    // dichter bij de uitgang van de stappenlijn dan zijn bovenkant) en loopt het
    // pad eerst omhoog en daarna weer omlaag. Alleen bij een horizontaal stuk
    // zegt de y-richting niets; daar kiezen we wel op nabijheid.
    for (const r of rauw) {
        const p = r.punten;
        if (!horizontaal(p) && p[0][1] > p[p.length - 1][1]) r.punten = [...p].reverse();
    }

    // Het begin en het eind van het pad net buiten beeld trekken, zodat het icoon
    // van het scherm komt en er weer af gaat in plaats van op de rand te
    // verschijnen. Alleen als dat punt al tegen een schermrand ligt; een pad dat
    // ergens midden op de pagina begint blijft waar het is.
    if (rauw.length) {
        const naarBuiten = (punt) => {
            if (punt[0] <= rand + 1) punt[0] = -OVERSCHOT;
            else if (punt[0] >= breedte - rand - 1) punt[0] = breedte + OVERSCHOT;
        };
        naarBuiten(rauw[0].punten[0]);
        const laatste = rauw[rauw.length - 1].punten;
        naarBuiten(laatste[laatste.length - 1]);
    }

    const stukken = [];
    const elementen = [];
    let vorigEind = null;
    for (const r of rauw) {
        let punten = r.punten;
        if (vorigEind && horizontaal(punten)) {
            const naarBegin = Math.abs(vorigEind[0] - punten[0][0]);
            const naarEind = Math.abs(vorigEind[0] - punten[punten.length - 1][0]);
            if (naarEind < naarBegin) punten = [...punten].reverse();
        }
        const lengtes = [];
        let lengte = 0;
        for (let i = 1; i < punten.length; i++) {
            const d = afstand(punten[i - 1], punten[i]);
            lengtes.push(d);
            lengte += d;
        }
        // Twee stukken kunnen elkaar overlappen in plaats van kop aan staart te
        // liggen: bij een trap vallen de horizontale delen over elkaar heen. Ligt
        // het vorige eindpunt op dit stuk, knip het stuk daar dan af; anders zou
        // er een gat ontstaan waar de lijn gewoon doorloopt.
        if (vorigEind && afstand(vorigEind, punten[0]) > TOLERANTIE) {
            const raak = raakpunt(punten, vorigEind);
            if (raak !== null) {
                punten = punten.slice(raak.index);
                punten[0] = vorigEind;
            }
        }
        if (vorigEind && afstand(vorigEind, punten[0]) > TOLERANTIE) {
            stukken.push(maakStuk(verbindingsPunten(vorigEind, punten[0]), true));
        }
        stukken.push(maakStuk(punten, false));
        // y-bereik uit de punten die we toch al hebben, zodat het tekenen later
        // geen getBoundingClientRect per scroll hoeft te doen.
        let top = Infinity, onder = -Infinity;
        for (const q of punten) {
            if (q[1] < top) top = q[1];
            if (q[1] > onder) onder = q[1];
        }
        elementen.push({ el: r.el, top, onder });
        vorigEind = punten[punten.length - 1];
    }

    const totaal = stukken.reduce((som, s) => som + s.lengte, 0);
    return { stukken, elementen, totaal };
}

/** Zoekt het punt op `d` pixels langs het pad. Valt dat in een gat, dan null. */
function puntOp(pad, d) {
    let gelopen = 0;
    for (const stuk of pad.stukken) {
        if (d <= gelopen + stuk.lengte) {
            // Tussen twee blokken staat geen lijn, dus daar hoort ook geen icoon.
            if (stuk.verbinding) return null;
            let over = d - gelopen;
            for (let i = 0; i < stuk.lengtes.length; i++) {
                if (over <= stuk.lengtes[i]) {
                    const a = stuk.punten[i], b = stuk.punten[i + 1];
                    const f = stuk.lengtes[i] === 0 ? 0 : over / stuk.lengtes[i];
                    return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f];
                }
                over -= stuk.lengtes[i];
            }
            return stuk.punten[stuk.punten.length - 1];
        }
        gelopen += stuk.lengte;
    }
    const laatste = pad.stukken[pad.stukken.length - 1];
    return laatste ? laatste.punten[laatste.punten.length - 1] : null;
}

/**
 * Het pad als keyframes met `translate3d`. Bewust geen `offset-path`: die
 * animeert `offset-distance`, en dat rekent Chrome op de main thread uit. Bij
 * een druk beeld bevriest het icoon dan en springt het daarna bij. `transform`
 * draait wel op de compositor, dus het icoon blijft meelopen met de scroll.
 * Elk punt krijgt het percentage van zijn afstand langs het pad, zodat de
 * snelheid over de hele lijn gelijk blijft.
 */
/**
 * Soepeler laten lopen dan recht evenredig met de scroll. Een
 * `animation-timing-function` werkt hier niet: CSS past die toe op elk
 * interval tussen twee keyframes, en met tientallen padpunten krijg je dus
 * evenveel versnellingen achter elkaar. In plaats daarvan verschuiven we de
 * TIJDSTIP van elk keyframe: het punt dat op 50% van de lijn ligt, krijgt de
 * tijd waarop de curve die 50% bereikt.
 */
const CURVES = {
    zacht: (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2),
    uit: (t) => 1 - Math.pow(1 - t, 3),
    in: (t) => t * t * t,
};

/** Bij welke tijd bereikt de curve deze voortgang? Omkeren gaat het snelst met bisectie. */
function curveTijd(curve, voortgang) {
    let laag = 0;
    let hoog = 1;
    for (let i = 0; i < 24; i++) {
        const mid = (laag + hoog) / 2;
        if (curve(mid) < voortgang) laag = mid; else hoog = mid;
    }
    return (laag + hoog) / 2;
}

function padKeyframes(pad, oorsprong, curveNaam) {
    const curve = CURVES[curveNaam] || null;
    const regels = [];
    let gelopen = 0;
    for (const stuk of pad.stukken) {
        let lokaal = 0;
        for (let i = 0; i < stuk.punten.length; i++) {
            if (i > 0) lokaal += stuk.lengtes[i - 1];
            const punt = stuk.punten[i];
            const deel = pad.totaal > 0 ? (gelopen + lokaal) / pad.totaal : 0;
            const pct = (curve ? curveTijd(curve, deel) : deel) * 100;
            const x = (punt[0] - oorsprong.left).toFixed(1);
            const y = (punt[1] - oorsprong.top).toFixed(1);
            regels.push(`${pct.toFixed(3)}% { transform: translate3d(${x}px, ${y}px, 0) translate(-50%, -50%); }`);
        }
        gelopen += stuk.lengte;
    }
    return regels.join(" ");
}

/**
 * De gaten tussen twee lijnstukken zitten wél in het pad (anders klopt de
 * timing niet) maar horen niet zichtbaar te zijn. Vandaar een tweede animatie
 * die het icoon daar uitzet, met een haartje verschil zodat het hard schakelt.
 */
function zichtKeyframes(pad) {
    const stappen = [];
    let gelopen = 0;
    for (const stuk of pad.stukken) {
        if (stuk.verbinding && pad.totaal > 0) {
            const van = (gelopen / pad.totaal) * 100;
            const tot = ((gelopen + stuk.lengte) / pad.totaal) * 100;
            stappen.push([van, tot]);
        }
        gelopen += stuk.lengte;
    }
    if (!stappen.length) return "from, to { opacity: 1; }";

    // Begint het pad met een gat, dan staat het icoon meteen uit; eindigt het
    // ermee, dan blijft het uit tot het eind. Anders zou de afsluitende regel op
    // 100% het laatste gat weer zichtbaar maken.
    const startInGat = stappen.length && stappen[0][0] <= 0.01;
    const eindigtInGat = stappen.length && stappen[stappen.length - 1][1] >= 99.99;

    const regels = [`0% { opacity: ${startInGat ? 0 : 1}; }`];
    for (const [van, tot] of stappen) {
        if (van > 0.01) regels.push(`${Math.max(van - 0.01, 0).toFixed(2)}% { opacity: 1; }`);
        regels.push(`${van.toFixed(2)}% { opacity: 0; }`);
        regels.push(`${tot.toFixed(2)}% { opacity: 0; }`);
        if (tot < 99.99) regels.push(`${(tot + 0.01).toFixed(2)}% { opacity: 1; }`);
    }
    if (!eindigtInGat) regels.push("100% { opacity: 1; }");
    return regels.join(" ");
}

/**
 * Sierlijnen die bij geen enkele volger horen komen op zodra ze in beeld staan.
 * Zonder dit blijven ze op `opacity: 0` uit animations.css hangen, want die
 * klasse wordt normaal door een volger gezet. Geldt ook voor een pagina zónder
 * reizend icoon: de rail van een stappenblok hoort daar gewoon zichtbaar te zijn.
 */
function toonLosseLijnen(volgers) {
    const los = [...document.querySelectorAll(KIES_SEGMENT)].filter(
        (el) => !(volgers || []).some((v) => v.bevat(el)),
    );
    if (!los.length) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        los.forEach((el) => el.classList.add("lijn-getekend"));
        return;
    }

    const waarnemer = new IntersectionObserver((rijen, zelf) => {
        for (const rij of rijen) {
            if (!rij.isIntersecting) continue;
            rij.target.classList.add("lijn-getekend");
            zelf.unobserve(rij.target);
        }
    }, { rootMargin: "0px 0px -10% 0px" });
    los.forEach((el) => waarnemer.observe(el));
}

function start() {
    const iconen = document.querySelectorAll(KIES_ICOON);
    if (!iconen.length) {
        // Geen reizend icoon op deze pagina, maar er kunnen wel sierlijnen staan.
        toonLosseLijnen();
        return;
    }

    const stil = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // Lokaal staan de markers standaard aan, want daar wordt de animatie
    // afgesteld. Uitzetten met ?lijndebug=uit; op elke andere host aanzetten
    // met ?lijndebug.
    /**
     * De markers en de beeldtijdmeter staan uit. Aanzetten met ?lijndebug in de
     * URL, op elke omgeving. Dan verschijnen: twee vaste scroller-lijnen, per
     * blok een start- en eindlijn met percentage en scrollstand, en rechts een
     * teller met de fps en de stand van elke animatie.
     *
     * Houd hem uit terwijl je beoordeelt of iets vloeiend loopt: de overlay
     * draait zelf een lus per beeld en meet dan wat ze veroorzaakt.
     */
    /**
     * Markers en beeldtijdmeter staan uit. Aanzetten met ?lijndebug in de URL,
     * op elke omgeving: dan verschijnen de twee scroller-lijnen, per blok een
     * start- en eindlijn met percentage en scrollstand, en een teller met de
     * fps en de stand van elke animatie. ?lijnrange=2:25-75 probeert een bereik
     * zonder de code te wijzigen.
     *
     * Houd hem uit terwijl je beoordeelt of iets vloeiend loopt: de overlay
     * draait zelf een lus per beeld.
     */
    const DEBUG = (() => {
        const v = new URLSearchParams(window.location.search).get("lijndebug");
        return v !== null && v !== "uit" && v !== "0";
    })();
    // Zonder scroll-timelines zou het icoon op het begin van zijn pad blijven
    // staan; dan liever geen icoon. Bij minder beweging idem.
    const kanAnimeren = CSS.supports("animation-timeline", "view()");
    /**
     * Het bereik van een blok komt uit drie plaatsen, in deze volgorde:
     *   ?lijnrange=2:25-75  per blok, om te proberen zonder de code te wijzigen
     *   ?lijnrange=25,75    voor alle blokken tegelijk
     *   data-line-range     vast ingesteld op het blok zelf, via `bereik` op de
     *                       include van _components/line-icon
     * Staat er niets, dan rekent het script uit wat er te scrollen valt.
     */
    const urlBereik = (() => {
        const v = new URLSearchParams(window.location.search).get("lijnrange");
        if (!v) return { alle: null, perBlok: {} };
        if (v.includes(":")) {
            const perBlok = {};
            for (const deel of v.split(",")) {
                const [n, waarden] = deel.split(":");
                const [van, tot] = (waarden || "").split("-").map((x) => parseFloat(x));
                if (Number.isFinite(van) && Number.isFinite(tot)) perBlok[Number(n)] = [van, tot];
            }
            return { alle: null, perBlok };
        }
        const d = v.split(",").map((x) => parseFloat(x));
        return {
            alle: Number.isFinite(d[0]) && Number.isFinite(d[1]) ? [d[0], d[1]] : null,
            perBlok: {},
        };
    })();

    function ingesteldBereik(nummer, wikkel) {
        if (urlBereik.perBlok[nummer]) return urlBereik.perBlok[nummer];
        if (urlBereik.alle) return urlBereik.alle;
        const eigen = wikkel.dataset.lineRange;
        if (!eigen) return null;
        const d = eigen.split(/[,-]/).map((x) => parseFloat(x));
        return Number.isFinite(d[0]) && Number.isFinite(d[1]) ? [d[0], d[1]] : null;
    }

    /**
     * `cover 0%` ligt op de scrollstand waar het blok de onderrand raakt, en dat
     * is voor een blok bovenaan de pagina een negatieve stand: onbereikbaar, dus
     * begint het icoon daar al halverwege zijn lijn. Hetzelfde aan het eind voor
     * een blok onderaan. Daarom knippen we het bereik af op wat echt te scrollen
     * valt: zo staat het icoon bij stilstand altijd op zijn beginpunt buiten beeld
     * en haalt het ook altijd het eindpunt.
     */
    function berekenBereik(wortel, nummer, wikkel, padTop, padOnder) {
        const vast = ingesteldBereik(nummer, wikkel);
        // De tijdlijn hangt aan het blok, maar het pad mag daarbuiten liggen: bij
        // een aanloop naar boven of een doorloop naar het blok erna. Percentages
        // buiten 0 en 100 zijn toegestaan en wijzen dan naar die plekken.
        const vak = documentRect(wortel);
        const dekLengte = vak.bottom - vak.top + vensterHoogte;
        const begin = (Number.isFinite(padTop) ? padTop : vak.top) - vensterHoogte;
        const eind = Number.isFinite(padOnder) ? padOnder : vak.bottom;
        const lengte = eind - begin;
        const naarPct = (scroll) => ((scroll - (vak.top - vensterHoogte)) / dekLengte) * 100;
        const maxScroll = document.documentElement.scrollHeight - vensterHoogte;
        const startScroll = Math.max(Math.min(begin, maxScroll), 0);
        const eindScroll = Math.max(Math.min(eind, maxScroll), 0);
        if (vast) {
            // Een ingestelde waarde telt als percentage van de doorloop van het
            // blok, maar wordt net als een berekende afgeknipt op wat er te
            // scrollen valt: een blok onderaan de pagina haalt zijn 100% anders
            // nooit. De markers wijzen daardoor dezelfde plekken aan.
            const vanScroll = Math.max(Math.min(begin + (lengte * vast[0]) / 100, maxScroll), 0);
            const totScroll = Math.max(Math.min(begin + (lengte * vast[1]) / 100, maxScroll), 0);
            const vanPct = lengte > 0 ? ((vanScroll - begin) / lengte) * 100 : vast[0];
            const totPct = lengte > 0 ? ((totScroll - begin) / lengte) * 100 : vast[1];
            return {
                css: `cover ${naarPct(vanScroll).toFixed(2)}% cover ${naarPct(totScroll).toFixed(2)}%`,
                startScroll: vanScroll,
                eindScroll: totScroll,
                van: vanPct, tot: totPct, bron: "ingesteld",
            };
        }
        if (lengte <= 0 || !(eindScroll > startScroll)) {
            return { css: "cover 0% cover 100%", startScroll, eindScroll, van: 0, tot: 100, bron: "standaard" };
        }
        const van = ((startScroll - begin) / lengte) * 100;
        const tot = ((eindScroll - begin) / lengte) * 100;
        return { css: `cover ${naarPct(startScroll).toFixed(2)}% cover ${naarPct(eindScroll).toFixed(2)}%`,
            startScroll, eindScroll, van, tot, bron: "berekend" };
    }
    if (stil || !kanAnimeren) {
        document.querySelectorAll(KIES_SEGMENT).forEach((el) => el.classList.add("lijn-getekend"));
        document.querySelectorAll(KIES_ICOON).forEach((el) => { el.hidden = true; });
        return;
    }
    let vensterHoogte = document.documentElement.clientHeight;

    // De beweging loopt als scroll-gestuurde CSS-animatie op de compositor: geen
    // JS tijdens het scrollen, dus het icoon kan niet achterlopen op de pagina.
    // Alle keyframes van alle blokken staan in dit ene stijlblad.
    const blad = document.createElement("style");
    document.head.appendChild(blad);
    const keyframes = new Map();
    const schrijfBlad = () => {
        blad.textContent = [...keyframes.values()].join("\n");
    };

    // Eén vaste laag voor alle markers, plus de twee scroller-lijnen en de meter.
    function lijnDebugLaag() {
        if (document.getElementById("lijn-markers")) return;
        const laag = document.createElement("div");
        laag.id = "lijn-markers";
        laag.style.cssText = "position:fixed;inset:0;z-index:2147483000;pointer-events:none";
        document.body.appendChild(laag);
        const merk = (kl) => `position:absolute;background:${kl};color:#fff;`
            + "font:11px/1.5 ui-monospace,monospace;padding:1px 5px;border-radius:2px;white-space:nowrap";
        const vast = document.createElement("div");
        vast.innerHTML =
            `<div style="position:absolute;left:0;bottom:0;width:100%;border-bottom:2px dashed #16a34a">` +
            `<span style="${merk("#16a34a")};left:8px;bottom:3px">scroller-start</span></div>` +
            `<div style="position:absolute;left:0;top:0;width:100%;border-top:2px dashed #dc2626">` +
            `<span style="${merk("#dc2626")};left:8px;top:3px">scroller-end</span></div>`;
        laag.appendChild(vast);
    }

    /** Eén icoon met het pad van zijn eigen blok. */
    function maakVolger(icoon, nummer) {
        // De wikkel geeft de positie waarop het icoon staat, zijn ouder is het blok.
        const wikkel = icoon.parentElement;
        const wortel = wikkel && wikkel.parentElement;
        if (!wortel) return null;

        const naam = `--lijn-${nummer}`;
        // Met `doorloop` telt ook de lijn van het volgende blok mee, zodat één icoon
        // over beide loopt in plaats van twee die naast elkaar bewegen. Het is niet
        // altijd de directe buur: er kan een blok zonder lijn tussen staan.
        const doorloopNaar = (() => {
            if (!("lineDoor" in wikkel.dataset)) return null;
            let buur = wortel.nextElementSibling;
            while (buur) {
                if (buur.querySelector(KIES_SEGMENT)) return buur;
                buur = buur.nextElementSibling;
            }
            return null;
        })();
        const wortels = doorloopNaar ? [wortel, doorloopNaar] : [wortel];
        if (doorloopNaar) {
            doorloopNaar.querySelectorAll(KIES_ICOON).forEach((el) => {
                el.hidden = true;
                if (el.parentElement) el.parentElement.dataset.lineOvergenomen = "1";
            });
        }
        let pad = null;
        let lijnenKlaar = stil;
        let teTekenen = [];
        let zichtbaar = null;
        let padTop = 0;
        let padOnder = 0;
        let bereik = null;
        let markers = null;
        // Antwoord van kanAchter(), één keer per herbouw. Zowel verlengAanloop als
        // legLagen vraagt het op, en de eerste wijzigt het pad, dus zonder cache
        // meten die twee op een ander beginpunt.
        let achterKan = null;

        // Het opkomen van het icoon gaat via de wikkel, want de opacity van het
        // icoon zelf is van de gat-animatie.
        const zetZichtbaar = (aan) => {
            if (zichtbaar === aan) return;
            zichtbaar = aan;
            wikkel.style.opacity = aan ? "1" : "0";
        };

        const zetAnimatie = () => {
            if (!pad || !pad.stukken.length || pad.totaal < 1) {
                keyframes.delete(nummer);
                schrijfBlad();
                icoon.style.animationName = "none";
                wortel.style.viewTimelineName = "none";
                return;
            }

            const oorsprong = documentRect(wikkel);
            keyframes.set(nummer, `@keyframes lijn-pad-${nummer} { ${padKeyframes(pad, oorsprong, wikkel.dataset.lineEase)} }\n`
                + `@keyframes lijn-zicht-${nummer} { ${zichtKeyframes(pad)} }`);
            schrijfBlad();

            // De timeline hangt aan het blok: 0% is het blok dat onderaan in beeld
            // komt, 100% is het blok dat er bovenaan uit gaat.
            wortel.style.viewTimelineName = naam;
            icoon.style.animationName = `lijn-pad-${nummer}, lijn-zicht-${nummer}`;
            icoon.style.animationTimeline = `${naam}, ${naam}`;
            bereik = berekenBereik(wortel, nummer, wikkel, padTop, padOnder);
            icoon.style.animationRange = `${bereik.css}, ${bereik.css}`;
            icoon.style.animationTimingFunction = "linear, linear";
            icoon.style.animationFillMode = "both, both";
        };

        // De volgorde van het pad is de volgorde van tekenen, met een korte
        // vertraging per stuk zodat het als één beweging leest.
        const tekenZichtbare = (y) => {
            if (!teTekenen.length) return;
            const onderkant = y + vensterHoogte;
            let beurt = 0;
            teTekenen = teTekenen.filter(({ el, top, onder }) => {
                if (onder <= y || top >= onderkant) return true;
                const wacht = Math.min(beurt++, 6) * 90;
                window.setTimeout(() => {
                    el.classList.add("lijn-getekend");
                    // Het icoon mag erop zodra het eerste stuk staat.
                    window.setTimeout(() => { lijnenKlaar = true; zetZichtbaar(true); }, 700);
                }, wacht);
                return false;
            });
        };

        const tekenLijnen = (lijst) => {
            // Stukken die het pad niet kon meten staan op opacity 0 en zouden anders nooit opkomen.
            const inPad = new Set((lijst || []).map(({ el }) => el));
            wortel.querySelectorAll(KIES_SEGMENT).forEach((el) => {
                if (!inPad.has(el)) el.classList.add("lijn-getekend");
            });
            if (stil || !lijst || !lijst.length) {
                lijnenKlaar = true;
                teTekenen = [];
                return;
            }
            teTekenen = lijst.filter(({ el }) => !el.classList.contains("lijn-getekend"));
            tekenZichtbare(window.scrollY);
        };

        // Enkel nog voor het opkomen van de lijnen; de plaats van het icoon komt
        // van de CSS-animatie.
        const plaats = () => {
            tekenZichtbare(window.scrollY);
            zetZichtbaar(lijnenKlaar && !!pad);
        };

        // Het pad rechtdoor verlengen voorbij zijn beginpunt, zodat het icoon
        // komt aanlopen in plaats van op de rand van het blok te ontstaan.
        // Standaard naar boven; `aanloopKant` kan er links of rechts van maken,
        // voor een lijn die horizontaal begint.
        const verlengAanloop = () => {
            const aanloop = parseFloat(wikkel.dataset.lineAanloop);
            if (!Number.isFinite(aanloop) || aanloop <= 0 || !pad || !pad.stukken.length) return;
            const kant = wikkel.dataset.lineAanloopKant || "boven";
            const stuk = pad.stukken[0];
            const kop = stuk.punten[0];
            const verschuiving = {
                boven: [0, -aanloop],
                onder: [0, aanloop],
                links: [-aanloop, 0],
                rechts: [aanloop, 0],
            }[kant] || [0, -aanloop];
            // Valt er niets achter te verbergen, dan wordt de aanloop een los
            // gatstuk: de timing blijft, het icoon is er niet te zien.
            if ("lineAchter" in wikkel.dataset && !kanAchter()) {
                const vanaf = [kop[0] + verschuiving[0], kop[1] + verschuiving[1]];
                pad.stukken.unshift(maakStuk([vanaf, [kop[0], kop[1]]], true));
                pad.totaal += aanloop;
                if (pad.elementen.length && verschuiving[1] < 0) pad.elementen[0].top += verschuiving[1];
                return;
            }
            stuk.punten.unshift([kop[0] + verschuiving[0], kop[1] + verschuiving[1]]);
            stuk.lengtes.unshift(aanloop);
            stuk.lengte += aanloop;
            pad.totaal += aanloop;
            // Het y-bereik meegroeien, anders klopt de plek van de markers niet.
            if (pad.elementen.length && verschuiving[1] < 0) pad.elementen[0].top += verschuiving[1];
            if (pad.elementen.length && verschuiving[1] > 0) pad.elementen[0].onder += verschuiving[1];
        };

        // Het pad aan het eind doortrekken, zodat het icoon doorloopt voorbij de
        // laatste lijn in plaats van erop te blijven staan.
        const verlengUitloop = () => {
            const uitloop = parseFloat(wikkel.dataset.lineUitloop);
            if (!Number.isFinite(uitloop) || uitloop <= 0 || !pad || !pad.stukken.length) return;
            const kant = wikkel.dataset.lineUitloopKant || "onder";
            const stuk = pad.stukken[pad.stukken.length - 1];
            const staart = stuk.punten[stuk.punten.length - 1];
            const verschuiving = {
                boven: [0, -uitloop], onder: [0, uitloop],
                links: [-uitloop, 0], rechts: [uitloop, 0],
            }[kant] || [0, uitloop];
            // Net als bij de aanloop: valt er niets onder te verdwijnen, dan wordt
            // het uitloopstuk een gat. Het icoon loopt door maar is er niet te zien.
            const gatNaHetPad = ("lineOnder" in wikkel.dataset && !kanOnder())
                || ("lineAchterFooter" in wikkel.dataset && !kanAchterFooter());

            if (gatNaHetPad) {
                const tot = [staart[0] + verschuiving[0], staart[1] + verschuiving[1]];
                pad.stukken.push(maakStuk([[staart[0], staart[1]], tot], true));
                pad.totaal += uitloop;
                const laatsteEl = pad.elementen[pad.elementen.length - 1];
                if (laatsteEl && verschuiving[1] > 0) laatsteEl.onder += verschuiving[1];
                return;
            }
            stuk.punten.push([staart[0] + verschuiving[0], staart[1] + verschuiving[1]]);
            stuk.lengtes.push(uitloop);
            stuk.lengte += uitloop;
            pad.totaal += uitloop;
            const laatste = pad.elementen[pad.elementen.length - 1];
            if (laatste && verschuiving[1] > 0) laatste.onder += verschuiving[1];
        };

        /**
         * Het pad mag buiten het blok liggen, en dan moet het icoon soms achter
         * de buren verdwijnen en soms er juist overheen. Eén z-index op het blok
         * volstaat daar niet voor: buren die later in de DOM staan winnen bij
         * gelijke waarde. Vandaar een vaste ladder.
         *
         *   2  het blok erboven en de footer, waar het icoon onder hoort
         *   1  dit blok zelf, met het icoon erin
         *   0  het blok erna waar de lijn in doorloopt, waar het icoon boven hoort
         *
         * Een gezette waarde wordt nooit gewist: herbouw() draait ook bij resize
         * en zodra de beelden binnen zijn.
         */
        const laag = (el, waarde) => {
            if (!el) return;
            if (getComputedStyle(el).position === "static") el.style.position = "relative";
            el.style.zIndex = String(waarde);
        };

        /**
         * `achter` werkt alleen als het blok erboven een eigen achtergrondkleur
         * heeft; anders zweeft het icoon zichtbaar in de witruimte. Is dat zo,
         * dan telt het aanloopstuk als gat: het icoon komt wel aanlopen, maar is
         * pas te zien waar de lijn begint.
         */
        const kanAchter = () => {
            if (achterKan !== null) return achterKan;
            achterKan = bepaalAchter();
            return achterKan;
        };

        const bepaalAchter = () => {
            const vorige = wortel.previousElementSibling;
            if (!vorige) return false;

            // Draagt het blok erboven zelf een reizend icoon, dan wil dat op zijn
            // beurt achter zíjn voorganger verdwijnen. Die twee wensen passen niet
            // in één ladder: het blok erboven zou tegelijk boven ons moeten liggen
            // en onder zijn eigen voorganger. Zonder deze uitzondering wint de
            // laatste die de laag zet, en op /werken-bij-geopower/hvac-technieker
            // kwam het icoon van "Wie ben jij?" daardoor bovenop het blok erboven
            // te staan in plaats van erachter. De onderste van de twee laat het
            // varen en valt terug op een gat: het icoon loopt aan maar is pas te
            // zien waar zijn eigen lijn begint.
            const icoonBoven = vorige.querySelector(KIES_ICOON);

            if (icoonBoven && !icoonBoven.hidden) return false;

            if (getComputedStyle(vorige).backgroundColor !== "rgba(0, 0, 0, 0)") return true;
            // Een blok kan zelf doorzichtig zijn en toch een gekleurd paneel dragen,
            // zoals de EPOS-kaart boven het citaat. Dan valt er wel degelijk iets
            // achter te verdwijnen, maar alleen als dat paneel de hele aanloop
            // afdekt: dekt het er maar een stuk van, dan zou het icoon halverwege
            // in de witruimte opduiken en is een gat het betere gedrag. Geen
            // elementFromPoint: bij een herbouw staat het blok vaak buiten beeld
            // en geeft die niets terug.
            const kop = pad && pad.stukken.length ? pad.stukken[0].punten[0] : null;
            if (!kop) return false;
            const aanloop = parseFloat(wikkel.dataset.lineAanloop) || 0;
            const onderrand = documentRect(vorige).bottom - 1;
            const panelen = Array.from(vorige.querySelectorAll("*"))
                .filter((el) => getComputedStyle(el).backgroundColor !== "rgba(0, 0, 0, 0)")
                .map(documentRect);
            const afgedekt = (y) => panelen.some((r) =>
                kop[0] >= r.left && kop[0] <= r.right && y >= r.top && y <= r.bottom);
            const stappen = aanloop > 0 ? 6 : 0;
            for (let i = 0; i <= stappen; i++) {
                if (!afgedekt(onderrand - (aanloop * i) / (stappen || 1))) return false;
            }
            return true;
        };

        /** Het blok waar het pad in eindigt, en dus waar een uitloop onder valt. */
        const blokNaHetPad = () => (doorloopNaar || wortel).nextElementSibling;

        const kanOnder = () => {
            const na = blokNaHetPad();
            if (!na) return false;
            return getComputedStyle(na).backgroundColor !== "rgba(0, 0, 0, 0)";
        };

        /**
         * `achterFooter` gaat ervan uit dat de footer het eerstvolgende is, en dat
         * klopt alleen als het pad in het laatste blok eindigt. Staat er nog een
         * blok achter, dan zakt het icoon daar juist bovenop in plaats van onder
         * de footer te verdwijnen. Het uitloopstuk wordt dan een gat, net als bij
         * `onder`: het icoon loopt door maar is er niet meer te zien.
         */
        const kanAchterFooter = () => !blokNaHetPad();

        const legLagen = () => {
            const achter = "lineAchter" in wikkel.dataset && kanAchter();
            const achterFooter = "lineAchterFooter" in wikkel.dataset && kanAchterFooter();
            const onder = "lineOnder" in wikkel.dataset && kanOnder();
            // Alleen een eigen laag maken als het icoon ergens achter moet. Bij
            // enkel een doorloop niet: dan wordt het blok een stacking context en
            // liften decoraties met een negatieve z-index mee over hun buren heen.
            // Zonder die context komt het icoon vanzelf boven, via de z-20 op zijn
            // wikkel.
            if (!achter && !achterFooter && !onder) return;
            laag(wortel, 1);
            if (achter) laag(wortel.previousElementSibling, 2);
            if (achterFooter) laag(document.querySelector("footer"), 2);
            if (onder) laag(blokNaHetPad(), 2);
            if (doorloopNaar) laag(doorloopNaar, 0);
        };

        const herbouw = () => {
            pad = bouwPad(wortels);
            achterKan = null;
            verlengAanloop();
            verlengUitloop();
            legLagen();
            if (!pad.stukken.length || pad.totaal < 1) {
                const elementen = pad ? pad.elementen : null;
                pad = null;
                zetZichtbaar(false);
                tekenLijnen(elementen);
            } else {
                padTop = Math.min(...pad.elementen.map((e) => e.top));
                padOnder = Math.max(...pad.elementen.map((e) => e.onder));
                tekenLijnen(pad.elementen);
            }
            zetAnimatie();
            tekenDebug();
            plaats();
        };

        // Markers in de stijl van ScrollTrigger, allemaal in één vaste laag zodat
        // ze nooit wegscrollen of achter een blok verdwijnen. Valt een grens
        // buiten beeld, dan plakt de marker aan de rand met een pijl erbij.
        function tekenDebug() {
            if (!DEBUG) return;
            lijnDebugLaag();
            // Een blok waarvan het icoon is overgenomen tekent geen markers: die
            // horen bij het traject van de volger die het overnam.
            if (!pad || icoon.hidden) {
                if (markers) { markers.remove(); markers = null; }
                return;
            }
            if (!markers) {
                markers = document.createElement("div");
                markers.dataset.lijnMarker = String(nummer);
                document.getElementById("lijn-markers").appendChild(markers);
            }
            const kleur = nummer % 2 ? "#c2410c" : "#1d4ed8";
            markers.innerHTML =
                `<div style="position:absolute;left:0;top:0;width:100%;border-top:2px solid #16a34a;will-change:transform">` +
                `<span style="position:absolute;right:8px;top:2px;background:#16a34a;color:#fff;` +
                `font:11px/1.5 ui-monospace,monospace;padding:1px 5px;border-radius:2px;white-space:nowrap">` +
                `<b style="color:${kleur}">&#9632;</b> blok ${nummer} START &middot; ${bereik ? bereik.van.toFixed(0) : 0}% &middot; scroll ${Math.round(bereik ? bereik.startScroll : 0)}</span></div>` +
                `<div style="position:absolute;left:0;top:0;width:100%;border-top:2px solid #dc2626;will-change:transform">` +
                `<span style="position:absolute;right:8px;top:2px;background:#dc2626;color:#fff;` +
                `font:11px/1.5 ui-monospace,monospace;padding:1px 5px;border-radius:2px;white-space:nowrap">` +
                `<b style="color:${kleur}">&#9632;</b> blok ${nummer} END &middot; ${bereik ? bereik.tot.toFixed(0) : 100}% &middot; scroll ${Math.round(bereik ? bereik.eindScroll : 0)}</span></div>`;
        }

        // Per beeld op hun plek zetten, want ze staan in een vaste laag.
        const plaatsMarkers = () => {
            if (!DEBUG || !markers || !bereik) return;
            // Alleen een transform schrijven: geen lezen van layout, dus geen
            // werk op de main thread dat de scroll zou vertragen.
            const zet = (el, docY) => {
                if (!el) return;
                const y = docY - window.scrollY;
                const geklemd = Math.max(0, Math.min(vensterHoogte - 2, y));
                el.style.transform = `translate3d(0, ${geklemd.toFixed(1)}px, 0)`;
                el.style.opacity = (y < 0 || y > vensterHoogte) ? "0.45" : "1";
            };
            zet(markers.firstElementChild, bereik.startScroll + vensterHoogte);
            zet(markers.lastElementChild, bereik.eindScroll);
        };

        // Stand van deze animatie in procenten, uit de animatie zelf.
        const stand = () => {
            const a = icoon.getAnimations().find((x) => (x.animationName || "").startsWith("lijn-pad"));
            if (!a || !a.timeline) return `blok ${nummer}: -`;
            const t = a.effect && a.effect.getComputedTiming();
            const p = t && typeof t.progress === "number" ? (t.progress * 100).toFixed(0) + "%" : "buiten";
            const b = bereik ? `${bereik.van.toFixed(0)}-${bereik.tot.toFixed(0)}` : "?";
            return `blok ${nummer}: ${p} <span style="opacity:.6">[${b}]</span>`;
        };

        return { herbouw, plaats, pad: () => pad, stand, plaatsMarkers,
            bevat: (el) => wortel.contains(el),
            staat: () => ({ padTop, padOnder, lijnenKlaar, totaal: pad && pad.totaal }) };
    }

    const volgers = [...iconen].map((ic, n) => maakVolger(ic, n + 1)).filter(Boolean);

    toonLosseLijnen(volgers);

    if (!volgers.length) return;

    const herbouwAlles = () => {
        vensterHoogte = document.documentElement.clientHeight;
        volgers.forEach((v) => v.herbouw());
    };
    const plaatsAlles = () => volgers.forEach((v) => v.plaats());

    herbouwAlles();

    // Eén lus voor alle markers plus de beeldtijden van deze machine. Headless
    // meet dat niet zinnig, dus dit hoort op een echt scherm gelezen te worden.
    if (DEBUG) {
        const meter = document.createElement("div");
        meter.style.cssText = "position:fixed;right:8px;top:50%;transform:translateY(-50%);z-index:2147483002;"
            + "background:rgba(0,0,0,.85);color:#fff;font:11px/1.6 ui-monospace,monospace;padding:7px 9px;"
            + "border-radius:4px;pointer-events:none;min-width:190px";
        document.body.appendChild(meter);
        let vorige = performance.now();
        let recent = [];
        let piek = 0;
        let piekBij = "";
        let laatsteTekst = 0;
        const tik = (nu) => {
            const dt = nu - vorige;
            vorige = nu;
            recent.push({ t: nu, dt });
            while (recent.length && nu - recent[0].t > 2000) recent.shift();
            const ergst = recent.reduce((m, r) => (r.dt > m ? r.dt : m), 0);
            if (ergst > piek) {
                piek = ergst;
                piekBij = `scroll ${Math.round(window.scrollY)}`;
            }
            // Markers verschuiven per beeld, maar dat is enkel een transform.
            volgers.forEach((v) => v.plaatsMarkers());
            // De tekst hoogstens vier keer per seconde: die uitlezingen kosten
            // layout en zouden meten wat ze zelf veroorzaken.
            if (nu - laatsteTekst > 250) {
                laatsteTekst = nu;
                const traag = recent.filter((r) => r.dt > 20).length;
                const kleur = ergst > 20 ? "#f87171" : "#4ade80";
                meter.innerHTML = `<b style="color:${kleur}">${(1000 / Math.max(dt, 1)).toFixed(0)} fps</b>`
                    + ` &middot; traagste ${ergst.toFixed(1)}ms`
                    + `<br>&gt;20ms in 2s: <b style="color:${traag ? "#f87171" : "#4ade80"}">${traag}</b>`
                    + `<br>piek: ${piek.toFixed(1)}ms <span style="opacity:.7">bij ${piekBij}</span>`
                    + `<hr style="border:0;border-top:1px solid #444;margin:4px 0">`
                    + volgers.map((v) => v.stand()).join("<br>");
            }
            requestAnimationFrame(tik);
        };
        requestAnimationFrame(tik);
    }

    // Debug-haak: met ?lijndebug in de URL lees je de opgebouwde paden uit.
    if (DEBUG) {
        window.lijnPad = () => volgers.map((v) => v.pad());
        window.lijnStaat = () => volgers.map((v) => v.staat());
        window.lijnStand = () => volgers.map((v) => v.stand());
        window.lijnMarkers = () => volgers.forEach((v) => v.plaatsMarkers());
    }

    // Enkel nog om de lijnen te laten opkomen; het icoon hangt aan de scroll-timeline.
    window.addEventListener("scroll", plaatsAlles, { passive: true });

    // De lijnen hangen aan het raster, dus bij een resize verandert het pad.
    let hertimer;
    window.addEventListener("resize", () => {
        clearTimeout(hertimer);
        hertimer = setTimeout(herbouwAlles, 150);
    }, { passive: true });

    // Beelden die later inladen verschuiven de pagina, dus dan opnieuw meten.
    window.addEventListener("load", herbouwAlles);

    // En blokken die zichzelf pas na `load` opbouwen verschuiven haar ook. De
    // slider zet zijn slides dan van onder elkaar naar naast elkaar, waardoor de
    // pagina in één keer duizenden pixels korter wordt. Alles wat eronder staat
    // houdt dan de scrollstanden van vóór die inklap: op /onze-oplossingen lag
    // het bereik van de onderste twee blokken op scroll 11354 tot 13328 terwijl
    // de pagina 4868 hoog is. Het icoon kwam daar dus nooit aan en de lijnen
    // bleven op `opacity: 0` staan.
    //
    // `resize` vangt dit niet: het venster verandert niet, alleen de inhoud.
    // Vandaar een ResizeObserver op de body, met dezelfde ontdendering als bij
    // resize. De drempel houdt het stil bij subpixelruis; herbouwAlles zelf
    // verandert de hoogte niet, dus het kan niet in een lus lopen.
    if ("ResizeObserver" in window) {
        let vorigeHoogte = document.documentElement.scrollHeight;
        new ResizeObserver(() => {
            const hoogte = document.documentElement.scrollHeight;
            if (Math.abs(hoogte - vorigeHoogte) < 2) return;
            vorigeHoogte = hoogte;
            clearTimeout(hertimer);
            hertimer = setTimeout(herbouwAlles, 150);
        }).observe(document.body);
    }
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
} else {
    start();
}
